import {Component, NgZone} from "@angular/core";
import {Loading, LoadingController, NavController, NavParams, ViewController} from "ionic-angular";
import {UserAddress} from "../../app/classes/user_address";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {POSTAL_CODE_LENGTH} from "../../app/constants/numbers";
import {IAddress} from "../../app/interfaces/address";
import {USER_ADDRESS_STORAGE} from "../../app/constants/storage";
import { Geolocation } from '@capacitor/geolocation';
import { Dialog } from '@capacitor/dialog';
import {Http} from "@angular/http";
import { AddressDataProvider } from "./AddressDataProvider";
import {StorageService} from "../../app/services/storage.service";
import {CUSTOMER_PAYLOAD} from "../../app/app.config";

/**
 * Default address form validations
 * @type {ValidatorFn}
 */
const DEFAULT_FORM_VALIDATE = Validators.compose([
  Validators.required
]);

/**
 * Form validations for postalcodes
 * @type {ValidatorFn}
 */
const POSTALCODE_FORM_VALIDATE = Validators.compose([
  Validators.required,
  Validators.pattern('[0-9]*'),
  Validators.maxLength(POSTAL_CODE_LENGTH),
  Validators.minLength(POSTAL_CODE_LENGTH)
]);

declare const google: any;

@Component({
  selector: 'page-add-address',
  templateUrl: 'add-address.html'
})
export class AddAddressPage {
  public submitAttempt = false;
  public addressForm: FormGroup;
  public loader: Loading;
  public selectedAddress : any;
  public baseAddress;
  public places;
  public addressTimer;
  public addressLoading = false;
  public autocompleteResults = [];
  public service = (typeof google !== "undefined" ? new google.maps.places.AutocompleteService() : false);

  constructor(public loadingCtrl: LoadingController,
    private http: Http, private storage: StorageService, public formBuilder: FormBuilder, public viewCtrl: ViewController, public navCtrl: NavController, public navParams: NavParams) {
    let address = this.navParams.get("address");
    if (address) { // edit mode
      this.baseAddress = address;
    } else {
      address = {};
    }
    this.addressForm = formBuilder.group({
      "address": `${address.street} ${address.streetnumber}, ${address.postalcode} ${address.location}`,
      firstname: [address.firstname || '', DEFAULT_FORM_VALIDATE],
      lastname: [address.lastname || '', DEFAULT_FORM_VALIDATE],
      postalcode: [address.postalcode || '', POSTALCODE_FORM_VALIDATE],
      street: [address.street || '', DEFAULT_FORM_VALIDATE],
      floor: [address.floor || '', DEFAULT_FORM_VALIDATE],
      streetnumber: [address.streetnumber || '', DEFAULT_FORM_VALIDATE],
      location: [address.location || '', DEFAULT_FORM_VALIDATE],
      identifier: [address.identifier || ''],
      lat: [address.lat || ''],
      lng: [address.lng || '']
    });

    this.places = this.addressForm.get("street").value
  }

  resolveAddress(prediction){
    this.loader = this.loadingCtrl.create({
      content: "Adresse wird geladen...",
    });
    this.loader.present();
    UserAddress.fromPrediction(this.http, prediction).then((address: any) => {
      this.loader.dismiss();
      this.selectedAddress = address;
      this.addressForm.setValue({
        "address": `${address.street} ${address.streetnumber}, ${address.postalcode} ${address.location}`,
        "street": address.street,
        "location": address.location,
        "postalcode": address.postalcode,
        "streetnumber": address.streetnumber,
        "floor": address.floor || "EG",
        "firstname": this.addressForm.get("firstname").value,
        "lastname": this.addressForm.get("lastname").value,
        "identifier": "",
        "lat": address.lat,
        "lng": address.lng
      });

      this.autocompleteResults = [];
    })
  }

  searchPlaces() {
    if (!this.service){ return; }
    if (!this.places){ return; }

    const me = this;
    this.addressLoading = true;
    this.autocompleteResults = [];
    if (this.addressTimer){ clearTimeout(this.addressTimer)}
    this.addressTimer = setTimeout(() => {
      this.service.getPlacePredictions({
        input: this.places,
        componentRestrictions: {country: 'DE'},
        location: new google.maps.LatLng(CUSTOMER_PAYLOAD.latitude || 50.28362, CUSTOMER_PAYLOAD.longitude || 8.27888),
        radius: 10 * 1000,
        types: ['geocode']
      }, function (predictions, status) {
        if (!predictions){
          me.autocompleteResults = [];
          me.addressLoading = false;
          return;
        }
        predictions.forEach(function (prediction) {
          me.autocompleteResults.push(prediction);
          me.addressLoading = false;

        });
      });
    }, 300);
  }


  saveAddress() {
    this.submitAttempt = true;
    if (this.addressForm.valid) {
      const userAddress: UserAddress = new UserAddress(this.addressForm.value as IAddress);

      if (!this.baseAddress) {
        userAddress.identifier = +new Date();
      } else {
        userAddress.identifier = this.baseAddress.identifier;
      }

      this.storage.get(USER_ADDRESS_STORAGE).then((addresses) => {
        if (!addresses) addresses = [];

        if (this.baseAddress) {
          addresses = addresses.map((address) => {
            if (this.baseAddress.identifier == address.identifier) {
              return userAddress;
            }
            return address;
          });
        } else {
          addresses.push(userAddress);
        }

        this.storage.set(USER_ADDRESS_STORAGE, addresses).then(() => {
          this.viewCtrl.dismiss(userAddress);
        });
      });
    }
  }

  findGPS() {
    this.loader = this.loadingCtrl.create({
      content: "Suche Standort...",
      duration: 15000
    });
    this.loader.present();
    Geolocation.getCurrentPosition().then((response) => {
      const coordinates = {
        latitude: response.coords.latitude,
        longitude: response.coords.longitude
      };

      this.http.get(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${coordinates.latitude},${coordinates.longitude}&sensors=true}`
      ).subscribe(
        result => this.receivedGPSLocation(result, response.coords),
        error => this.handleGPSError(error)
      )
    }).catch((error) => {
      this.loader.dismiss();
      Dialog.alert({
        message: "Dein Standort konnte nicht ermittelt werden",
        title: "GPS Fehler",
        buttonTitle: "Ok"
      });
    });
  }

  receivedGPSLocation(result, {latitude, longitude}) {
    const address = UserAddress.fromGeolocation(result.json().results[0]) as UserAddress;
    this.addressForm.setValue({
      "address": `${address.street} ${address.streetnumber}, ${address.postalcode} ${address.location}`,
      "street": address.street,
      "location": address.location,
      "postalcode": address.postalcode,
      "streetnumber": address.streetnumber,
      "firstname": this.addressForm.get("firstname").value,
      "lastname": this.addressForm.get("lastname").value,
      "identifier": "",
      "lat": latitude,
      "lng": longitude
    });
    this.loader.dismiss();
  }

  handleGPSError(err) {
    this.loader.dismiss();
    Dialog.alert({
      message: "Dein Standort konnte nicht ermittelt werden",
      title: "GPS Fehler",
      buttonTitle: "Ok"
    });
  }

  closeModal() {
    this.viewCtrl.dismiss();
  }
}



// WEBPACK FOOTER //
// ./src/pages/address/add_address.ts